/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import UiPill from 'components/Ui/UiPill'
import {OfferStatus} from 'types/enums/OfferStatus'
import {useTranslation} from 'react-i18next'

interface UiOfferStatusPillProps {
    status: OfferStatus | string
    children?: React.ReactNode
    className?: string
}

const UiOfferStatusPill: React.FC<UiOfferStatusPillProps> = ({
    status,
    children,
    className,
}) => {
    const { t } = useTranslation()

    const getStatusColors = () => {
        switch (status) {
            case OfferStatus.Draft:
                return 'pill-tone-yellow'
            case OfferStatus.New:
                return 'pill-tone-purple'
            case OfferStatus.Sent:
                return 'pill-tone-indigo'
            case OfferStatus.Accepted:
                return 'pill-tone-green'
            case OfferStatus.Finished:
                return 'pill-tone-blue'
            case OfferStatus.Reconfirm:
                return 'pill-tone-cyan'
            case OfferStatus.Rejected:
                return 'pill-tone-red'
            case OfferStatus.Expired:
                return 'pill-tone-orange'
            default:
                return 'pill-tone-gray'
        }
    }

    // Capitalize first letter if it's a simple string, or use translation if needed
    const label = (() => {
        if (!Object.values(OfferStatus).includes(status as OfferStatus)) {
            return status
        }

        if (status === OfferStatus.Reconfirm) {
            return t('Reconfirm')
        }

        return t(status.charAt(0).toUpperCase() + status.slice(1))
    })()

    return (
        <UiPill className={clsx('UiOfferStatusPill', getStatusColors(), className)}>
            {children || label}
        </UiPill>
    )
}

export default UiOfferStatusPill
